import type {
  Bonus,
  Buyer,
  Faq,
  IncludedItem,
  PracticePath,
  RecognizeItem,
  Testimonial,
  Volume,
} from '../types/content';

export const CHECKOUT_URL = 'https://apometrydeck-en.impultienda.ar/checkout/';

export const images = {
  deck: 'https://cdn.magicpatterns.com/uploads/58eGiVQiNuLom2m7bQcydU/APOMETRYDECK-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/q5bRSg6QMTZXjRKWC2KAYs/HELENA_DUARTE_APOMETRIA.png',
};

export const learningList = [
  'I watched videos.',
  'I saved techniques.',
  'I read about chakras.',
  'I discovered different practices.',
  'I collected rituals.',
];

export const systemLines = ['30 cards.', '5 practice paths.', '15 minutes or less.', 'One simple system.'];

export const beforeItems = [
  'You have dozens of saved videos and PDFs but no clear routine.',
  'You constantly switch between different techniques.',
  'You spend more time deciding what to practice than actually practicing.',
  'You know about chakras, harmonization, protection and other concepts, but don’t know how to organize them.',
  'You start a practice and lose consistency after a few days.',
  'Every session feels different because you are improvising.',
];

export const afterItems = [
  'You have 30 guided practices ready to use.',
  'You can choose from 5 clear practice paths.',
  'Each practice takes approximately 5–15 minutes.',
  'You know what to do from the moment you begin.',
  'You record what you noticed instead of relying on memory.',
  'You can repeat the system whenever you want to practice.',
];

export const results = [
  { value: '30', label: 'Guided practices' },
  { value: '5', label: 'Practice paths' },
  { value: '≤15', label: 'Minutes per practice' },
  { value: '1', label: 'Reusable practice system' },
];

export const recognizeItems: RecognizeItem[] = [
  {
    icon: 'help',
    quote: '“I’m interested in Apometry, but I never know where to start.”',
    body: 'You have learned about different concepts and techniques, but everything feels too broad.',
  },
  {
    icon: 'folder',
    quote: '“I have so much information saved, but I barely practice.”',
    body: 'Your phone is full of videos, screenshots and PDFs waiting for “the right moment.”',
  },
  {
    icon: 'shuffle',
    quote: '“Every time I want to practice, I ask myself what I should do.”',
    body: 'Instead of practicing, you end up searching for another technique.',
  },
  {
    icon: 'clock',
    quote: '“I don’t have hours every day.”',
    body: 'You want something realistic that fits into your normal routine.',
  },
  {
    icon: 'refresh',
    quote: '“I start practices, but I don’t stay consistent.”',
    body: 'You need a simple structure you can return to instead of starting from zero every time.',
  },
  {
    icon: 'compass',
    quote: '“I want to practice, not just learn more theory.”',
    body: 'You don’t necessarily need another course. You need a clear next step.',
  },
];

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'How to Use the Practice Deck',
    subtitle: 'Before you begin, I’ll show you exactly how to use the system.',
    intro: ['You’ll learn how to choose a card, prepare your practice, follow the sequence and close your session.'],
    includes: [
      'Quick-start guide',
      'How to choose a card',
      'Practice preparation',
      'Intention setting',
      'How to work with the five paths',
      'Opening routine',
      'Closing routine',
      'Practice guidelines',
      'First-session checklist',
    ],
  },
  {
    numeral: 'II',
    title: 'Observe',
    subtitle: 'Learn to notice before you intervene',
    intro: [
      'The first practice path is about observation.',
      'Instead of immediately trying to change your experience, you’ll learn to slow down and notice what is happening in your thoughts, emotions, sensations and attention.',
    ],
    includes: [
      'Observation practices',
      'Conscious breathing',
      'Sensation tracking',
      'Emotional observation',
      'Thought observation',
      'Pattern reflection',
      'Centering practices',
      'Observation cards',
      'Reflection prompts',
    ],
  },
  {
    numeral: 'III',
    title: 'Harmonize',
    subtitle: 'Create your structured practice',
    intro: [
      'This path brings together practices inspired by traditional energy-work concepts, visualization, attention and chakra-focused reflection.',
      'You’ll have simple sequences that you can follow without having to design the practice yourself.',
    ],
    includes: [
      'Chakra reference guide',
      'Harmonization practices',
      'Visualization exercises',
      'Breathing sequences',
      'Centering practices',
      'Intention setting',
      '5-minute practices',
      '15-minute practices',
      'Before-and-after reflection',
    ],
  },
  {
    numeral: 'IV',
    title: 'Release',
    subtitle: 'Work symbolically with what you want to let go',
    intro: [
      'Sometimes the hardest part is knowing how to create space for reflection and renewal.',
      'This volume gives you structured symbolic practices focused on release, detachment and personal reflection.',
    ],
    includes: [
      'Release practices',
      'Visualization',
      'Reflective writing',
      'Symbolic detachment',
      'Renewal exercises',
      'Intention reset',
      'Closure practices',
      'Reflection cards',
      'Personal observation prompts',
    ],
  },
  {
    numeral: 'V',
    title: 'Protect & Close',
    subtitle: 'End your practice with intention',
    intro: [
      'A practice doesn’t only need a beginning. It also needs a clear ending.',
      'I’ll show you simple closing and grounding routines designed to help you transition from your practice back into your day.',
    ],
    includes: [
      'Opening preparation',
      'Grounding practices',
      'Symbolic protection practices',
      'Closing routines',
      'Transition exercises',
      'Space organization',
      'Post-practice reflection',
      '5-minute closing protocol',
      'Closing checklist',
    ],
  },
];

export const finalVolume = {
  numeral: 'VI',
  title: 'The 30-Card Apometry Practice System',
  subtitle: 'This is where everything comes together.',
  intro: 'You’ll receive the complete collection of 30 guided practice cards, organized into five paths.',
};

export const practicePaths: PracticePath[] = [
  { name: 'Observe', count: '6 guided practices', color: 'bg-path-observe' },
  { name: 'Harmonize', count: '6 guided practices', color: 'bg-path-harmonize' },
  { name: 'Release', count: '6 guided practices', color: 'bg-path-release' },
  { name: 'Protect', count: '6 guided practices', color: 'bg-path-protect' },
  { name: 'Integrate', count: '6 guided practices', color: 'bg-path-integrate' },
];

export const cardSteps = ['Intention', 'Prepare', 'Practice', 'Close', 'Reflect'];

export const includedItems: IncludedItem[] = [
  {
    icon: 'layers',
    title: '30 Guided Practice Cards',
    body: 'A complete library of short practices organized around five different paths. Each card tells you what to focus on, how to begin, what to practice and what to reflect on afterward.',
  },
  {
    icon: 'checklist',
    title: 'Practice Checklists',
    body: 'Simple checklists for before, during and after your practice so you always know what comes next.',
  },
  {
    icon: 'map',
    title: 'Practice Path Map',
    body: 'A visual map showing the five paths and helping you decide where to begin.',
  },
  {
    icon: 'journal',
    title: 'Practice Observation Journal',
    body: 'Record your intention, the card you selected, what you noticed and what you want to observe next time.',
  },
  {
    icon: 'calendar',
    title: '30-Day Practice Calendar',
    body: 'Use one card per day and turn the 30-card system into a month-long structured practice.',
  },
  {
    icon: 'timer',
    title: 'The 15-Minute Practice Framework',
    body: 'A simple structure that helps you complete a focused session without spending hours preparing.',
  },
];

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: '21-Day Practice Journal™',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/wQNF85jo53LMdANeSWGeEW/BONUS_1_APOMETRYDECK-EN.png',
    description: ['Once you finish exploring the cards, continue your practice for another 21 days.'],
    includesLabel: 'You’ll receive',
    includes: [
      '21 tracking pages',
      'Daily intention',
      'Card used',
      'Pre-practice reflection',
      'Post-practice reflection',
      'Subjective observation scale',
      'Weekly review',
      '21-day closing exercise',
    ],
  },
  {
    number: 2,
    title: '30-Minute Practice Card Collection™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/kHdb8qXkiyTQkgFcMtJTPC/BONUS_2_APOMETRYDECK-EN.png',
    description: [
      'Sometimes you have more time.',
      'That’s why I’m giving you a second set of structured practice combinations designed for longer sessions.',
    ],
    includesLabel: 'Includes',
    includes: ['10 extended practice combinations', 'Suggested sequence', 'Preparation', 'Practice', 'Closing', 'Reflection'],
  },
  {
    number: 3,
    title: 'Apometry Practice Selector™',
    value: '$39',
    image: 'https://cdn.magicpatterns.com/uploads/1vYU9aEQfY4abuPNbARBjb/BONUS_3_APOMETRYDECK-EN.png',
    description: [
      'Not sure which path to choose?',
      'Use this simple visual selector to identify a starting point based on what you want to focus on during your practice.',
    ],
    includesLabel: 'Includes',
    includes: [
      'Practice decision map',
      'Five practice paths',
      'Quick-selection guide',
      'Beginner route',
      '5-minute route',
      '15-minute route',
    ],
  },
  {
    number: 4,
    title: 'Home Harmonization Mini-Kit™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/oacpv3qSnEfgNHyqsPgLLb/BONUS_4_APOMETRYDECK-EN.png',
    description: [
      'A practical collection of symbolic practices for creating a dedicated and intentional practice environment at home.',
    ],
    includesLabel: 'Includes',
    includes: [
      'Preparation checklist',
      'Space walkthrough',
      'Intention exercise',
      'Room-by-room reflection',
      'Symbolic harmonization practice',
      'Closing routine',
      'Home practice checklist',
    ],
  },
  {
    number: 5,
    title: '30-Day Spiritual Practice Calendar™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/bcga5eUhuvr5paS2m5c3zZ/BONUS_5_APOMETRYDECK-EN.png',
    description: [
      'Turn your cards into a complete month-long routine.',
      'Every day gives you a simple action: Observe → Breathe → Practice → Reflect → Record',
    ],
    includesLabel: 'Includes',
    includes: [
      '30 daily actions',
      'Daily practice sheet',
      'Habit tracker',
      'Weekly review',
      'Practice themes',
      'Monthly completion page',
    ],
  },
];

export const offerExtras = [
  '30 Guided Practice Cards',
  '5 Practice Paths',
  'Practice Checklists',
  'Practice Path Map',
  'Observation Journal',
  '30-Day Calendar',
  '15-Minute Practice Framework',
];

export const guaranteeItems = [
  'Open all 6 volumes',
  'Try the practice cards',
  'Complete the checklists',
  'Use the Practice Selector',
  'Start your journal',
  'Begin the 30-Day Calendar',
  'Explore all 5 bonuses',
];

export const testimonials: Testimonial[] = [
  {
    headline: '“I finally stopped collecting techniques.”',
    name: 'Sarah M.',
    country: 'United States',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/bb413eab-5541-4185-a3a1-47b823635f57.jpg',
    text: '“I had so many saved Apometry videos that I never knew what to practice. The cards changed that. I can choose one and start immediately.”',
  },
  {
    headline: '“The 15-minute format actually works for me.”',
    name: 'Daniela R.',
    country: 'Canada',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8650f8a6-c0e4-4236-b5ef-e49ecbf67383.jpg',
    text: '“What I love is that I don’t have to spend time deciding what to do. I choose a card, follow the steps and then write down what I noticed.”',
  },
  {
    headline: '“It feels completely different from another ebook.”',
    name: 'Margaret R.',
    country: 'United Kingdom',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a0555565-fd59-4a5e-94a7-c0684f7d8567.jpg',
    text: '“The cards make the practice feel simple. I can come back to the system whenever I want instead of starting over every time.”',
  },
  {
    headline: '“The journal became my favorite part.”',
    name: 'James T.',
    country: 'Australia',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b464f835-e54f-48ce-b60b-749e6127edc0.jpg',
    text: '“I started with one card a day and ended up using the journal to compare my observations over time. Everything feels much more organized.”',
  },
  {
    headline: '“I needed structure, not more information.”',
    name: 'Helena P.',
    country: 'United States',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a964c84d-67d7-476d-9067-10a4fa6f82da.jpg',
    text: '“I already had plenty of information about Apometry. What I was missing was a simple system that told me what to do next.”',
  },
];

export const authorSearchList = [
  'another technique.',
  'another video.',
  'another book.',
  'another ritual.',
  'another explanation.',
];

export const authorStructure = ['One intention.', 'One practice.', 'One observation.', 'One closing.', 'Then repeat.'];

export const authorSteps = ['Choose a card.', 'Follow the sequence.', 'Take a few minutes.', 'Observe.', 'Reflect.', 'Then come back tomorrow.'];

export const faqs: Faq[] = [
  {
    q: 'Do I need to have studied Apometry before?',
    a: ['No. The Apometry Practice Deck™ is designed as a practical introduction and can be used even if you’re completely new to Apometry-inspired practices.'],
  },
  {
    q: 'Is this a complete Apometry course?',
    a: [
      'No. This is not intended to replace comprehensive professional or traditional Apometry training.',
      'It is a practical system designed to help you organize and explore a personal practice.',
    ],
  },
  {
    q: 'Will I learn all the Laws of Apometry?',
    a: ['No. The Deck focuses on structured practice rather than attempting to teach every theoretical aspect or tradition of Apometry.'],
  },
  {
    q: 'How much time do I need?',
    a: ['Most practices are designed to take approximately 5–15 minutes. The system also includes longer practice combinations as a bonus.'],
  },
  { q: 'Do I need special materials?', a: ['No special equipment is required for the core practices.'] },
  {
    q: 'Do I need to be a medium?',
    a: ['No. The system is designed as a personal spiritual and reflective practice and does not require you to identify as a medium.'],
  },
  {
    q: 'How will I receive the material?',
    a: ['Immediately after payment, you’ll receive access to download the complete digital package.'],
  },
  {
    q: 'Can I download the files?',
    a: ['Yes. The materials are provided digitally so you can save them and use them whenever you want.'],
  },
  {
    q: 'How long will I have access?',
    a: ['Your purchase includes lifetime access to the materials included with your order.'],
  },
  { q: 'Is this a one-time payment?', a: ['Yes. You pay $27 USD once. There are no monthly fees.'] },
  {
    q: 'What happens if it isn’t right for me?',
    a: ['You can explore the system during the applicable 7-day guarantee period and request a refund if you decide it isn’t right for you.'],
  },
  {
    q: 'Does Apometry replace medical or psychological treatment?',
    a: [
      'No. The practices and concepts presented in this product are within the scope of spirituality, personal reflection and symbolic practices. They are not medical or psychological diagnosis or treatment and do not replace care from qualified professionals.',
    ],
  },
];

export const finalNeeds = [
  'You don’t need another hundred saved videos.',
  'You don’t need to learn everything before you begin.',
  'You don’t need to spend hours deciding what to do.',
];

export const buyers: Buyer[] = [
  { name: 'Valentina R.', country: 'Colombia' },
  { name: 'Mariana L.', country: 'Mexico' },
  { name: 'Lucía F.', country: 'Spain' },
  { name: 'Camila S.', country: 'Argentina' },
  { name: 'Sofía M.', country: 'Chile' },
  { name: 'Carlos P.', country: 'Peru' },
  { name: 'Isabella G.', country: 'Uruguay' },
  { name: 'Andrés V.', country: 'Ecuador' },
  { name: 'Gabriela T.', country: 'Costa Rica' },
  { name: 'Daniela H.', country: 'Dominican Republic' },
  { name: 'Fernanda C.', country: 'Guatemala' },
  { name: 'Javier N.', country: 'Venezuela' },
  { name: 'Paula A.', country: 'Paraguay' },
  { name: 'Ana B.', country: 'Puerto Rico' },
  { name: 'Diego M.', country: 'Bolivia' },
  { name: 'Ximena O.', country: 'Panama' },
];
